import { defineStore } from 'pinia'
import { ref } from 'vue'
// Types
import type {
  QuestionEntity,
  NoneWordTarget,
  SentenceRepetitionTarget,
  ExpressiveVerbMorphologyTarget,
  SpeechSoundProductionTarget,
  WordOrPhraseMatch,
  reportingGroupKeys,
  TranscriptionResult
} from '@/customTypes'

// Services
import { DeepgramService } from '@/services/DeepgramService'
import Logger from '@/services/Logger.js'

import { useAudioAnalyserStore } from '@/stores/AudioAnalyserStore'

const logger: any = new Logger('LiveSpeechStore', true)

export const useLiveSpeechStore = defineStore('liveSpeech', () => {
  const audioAnalyserStore = useAudioAnalyserStore()
  const itemsToMatch = ref<WordOrPhraseMatch | null>(null)
  const deepgramConnection = ref<any>(null)
  const deepgramService = new DeepgramService()

  const DEEPGRAM_PROCESSOR_NAME = 'deepgramAudioProcessor'

  /**
   * Sets the items to match within the transcription.
   * @param {WordOrPhraseMatch} items - An object containing words or phrases to match.
   */
  const setItemsToMatch = (items: WordOrPhraseMatch) => {
    itemsToMatch.value = items
  }

  /**
   * Starts the transcription process.
   * @param {function} onTranscript - Callback function to handle transcription results.
   * @param {function} onError - Optional callback function to handle connection errors.
   */
  const startTranscription = async (
    onTranscript: (result: TranscriptionResult) => void,
    onError?: (error: unknown) => void
  ) => {
    if (!audioAnalyserStore.hasAnalyserSetUp) {
      throw new Error('Audio analyser not set up. Call setupAudioAnalyser first.')
    }
    try {
      logger.info('Starting transcription')
      deepgramConnection.value = await deepgramService.createConnection(
        audioAnalyserStore.sampleRate,
        itemsToMatch.value ? itemsToMatch.value.keyWords : []
      )

      deepgramService.setupConnectionListeners(
        deepgramConnection.value,
        onTranscript,
        findMatches,
        onError
      )

      try {
        audioAnalyserStore.addCustomAudioProcessor(
          DEEPGRAM_PROCESSOR_NAME,
          (audioData: Float32Array) => {
            deepgramService.sendAudioData(deepgramConnection.value, audioData)
          }
        )
      } catch (processorError) {
        logger.error('Failed to attach Deepgram audio processor:', processorError)
        throw processorError
      }
    } catch (error) {
      logger.error('Error starting Deepgram transcription:', error)
      throw error
    }
  }

  /**
   * Stops the transcription process and cleans up resources.
   */
  const stopTranscription = () => {
    deepgramService.stopConnection(deepgramConnection.value)
    deepgramConnection.value = null
    audioAnalyserStore.removeCustomAudioProcessor(DEEPGRAM_PROCESSOR_NAME)
  }

  /**
   * Finds matches in the transcribed text based on the set word or phrases to match.
   * @param {string} text - The transcribed text to search for matches.
   * @returns {TranscriptionResult} The result containing the text and matches found.
   */
  const findMatches = (text: string): TranscriptionResult => {
    if (!itemsToMatch.value) return { text, matched: false }
    const words = text.toLowerCase().split(/\s+/)

    const item = itemsToMatch.value

    let matched: boolean
    let matchedText: string

    if (item.type === 'word') {
      const word = item.keyWords[0].toLowerCase()
      const alternativeMatches = item.alternatives ? item.alternatives[word] : []
      matched = words.some((w) => w === word || alternativeMatches?.includes(w))
      matchedText = matched
        ? words.find((w) => w === word || alternativeMatches?.includes(w)) || ''
        : ''
    } else {
      // phrase
      let keyWordIndex = 0
      let matchedWords: string[] = []
      const allKeyWordsMatched = true

      for (let i = 0; i < words.length; i++) {
        const word = words[i]
        const currentKeyWord = item.keyWords[keyWordIndex]?.toLowerCase()

        if (
          word === currentKeyWord ||
          (item.alternatives && item.alternatives[currentKeyWord]?.includes(word))
        ) {
          matchedWords.push(word)
          keyWordIndex++
          if (keyWordIndex === item.keyWords.length) break
        } else {
          keyWordIndex = 0
          matchedWords = []
        }
      }

      matched = keyWordIndex === item.keyWords.length
      matchedText = allKeyWordsMatched ? matchedWords.join(' ') : ''
    }

    return { text, matched, matchedText }
  }

  /**
   * Converts the current question target to Deepgram format.
   * @param {QuestionEntity} currentQuestion - The current question entity.
   * @param {reportingGroupKeys | undefined} subtestKey - The subtest key.
   * @returns {WordOrPhraseMatch | undefined} The converted target in Deepgram format.
   */
  const convertTargetFormat = (
    currentQuestion: QuestionEntity,
    subtestKey: reportingGroupKeys | undefined
  ): WordOrPhraseMatch | undefined => {
    const targets = currentQuestion.target || []
    if (targets.length === 0) return

    const firstTarget = targets[0]

    switch (subtestKey) {
      case 'nonword':
      case 'soundProduction': {
        return {
          type: 'word',
          keyWords: [(firstTarget as NoneWordTarget | SpeechSoundProductionTarget).target],
          alternatives: {
            [(firstTarget as NoneWordTarget | SpeechSoundProductionTarget).target]: [
              (firstTarget as NoneWordTarget | SpeechSoundProductionTarget).phoneticTarget || ''
            ]
          }
        }
      }
      case 'verbMorphology': {
        const mainTarget = (targets[0] as ExpressiveVerbMorphologyTarget).target
        const alternatives = targets
          .slice(1)
          .map((t) => (t as ExpressiveVerbMorphologyTarget).target)
        return {
          type: 'word',
          keyWords: [mainTarget],
          alternatives: { [mainTarget]: alternatives }
        }
      }
      case 'sentenceRepetition':
      default: {
        const words = (firstTarget as SentenceRepetitionTarget).target.split(' ')
        return {
          type: 'phrase',
          keyWords: words
        }
      }
    }
  }

  return {
    setItemsToMatch,
    startTranscription,
    stopTranscription,
    convertTargetFormat
  }
})
